import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-flexbox-grid';
import { useForm, Controller } from 'react-hook-form';
import { useToasts } from 'react-toast-notifications';
import Modal from 'common/components/modal/Modal';
import Button from 'common/components/button/Button';
import FormItem from 'common/components/formItem/FormItem';
import Label from 'common/components/label/Label';
import Input from 'common/components/input/Input';
import InputError from 'common/components/inputError/InputError';
import InputGroup from 'common/components/inputGroup/InputGroup';
import Select from 'common/components/select/Select';
import CreateSelect from 'common/components/select/CreateSelect';
import ToggleSwitch from 'common/components/toggleSwitch/ToggleSwitch';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE, removeAccents } from 'Config';
import GenericTypesService from 'api/genericTypes/GenericTypesService';
import UsersService from 'api/users/UsersService';
import { UsersSelectItemDto } from 'api/users/models/UserDto';
import VehicleExpensePlafondsService from 'api/vehicleExpensePlafonds/VehicleExpensePlafondsService';
import { PlafondTypeKind, VehicleExpensePlafondDto } from 'api/vehicleExpensePlafonds/models/VehicleExpensePlafondDto';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import styles from './ExpensePlafondModal.module.scss';

/**
 * Valor sentinela da tipologia "Manutenções" no seletor. As manutenções não são um tipo de despesa
 * (não têm generic_type), por isso não têm um Guid que as represente — ver PlafondTypeKind no backend.
 */
export const MAINTENANCE_OPTION_VALUE = 'MAINTENANCE';

type PlafondFormModel = {
    designation: string;
    monthlyLimit: number | null;
    typeIds: string[];
    notifyEnabled: boolean;
    /** Emails escolhidos. É o mesmo shape que a API lê e escreve. */
    contacts: string[];
};

type Props = {
    isVisible: boolean;
    vehicleId: string;
    /** Plafond a editar; ausente = criar. */
    plafond?: VehicleExpensePlafondDto | null;
    onClose: () => void;
    onSaved: () => void;
};

const ExpensePlafondModal: React.FC<Props> = ({ isVisible, vehicleId, plafond, onClose, onSaved }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const form = useForm<PlafondFormModel>({ shouldUnregister: false });
    const [typeOptions, setTypeOptions] = useState<SelectValueLabel[]>([]);
    const [contactOptions, setContactOptions] = useState<SelectValueLabel[]>([]);

    const notifyEnabled = form.watch('notifyEnabled');

    useEffect(() => {
        if (!isVisible) { return; }
        loadTypes();
        loadContactOptions();
        resetForm();
    }, [isVisible, plafond]);

    /**
     * Utilizadores da empresa como sugestões, mais os endereços já gravados neste plafond — que
     * podem ter sido escritos à mão e não corresponder a nenhum utilizador.
     */
    const loadContactOptions = async () => {
        try {
            const users = await UsersService.getAllForCompany();
            const options = (users || [])
                .filter((x: UsersSelectItemDto) => !!x.email)
                .map((x: UsersSelectItemDto) => ({
                    value: x.email || '',
                    label: x.realName ? `${x.realName} (${x.email})` : (x.email || ''),
                } as SelectValueLabel));

            const known = new Set(options.map(x => x.value));
            const extras = (plafond?.contacts || [])
                .filter(email => !known.has(email))
                .map(email => ({ value: email, label: email } as SelectValueLabel));

            setContactOptions([...options, ...extras]);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t load the users for plafond recipients', error);
            // Sem sugestões o campo continua a servir: escrevem-se os endereços à mão.
            setContactOptions((plafond?.contacts || []).map(email => ({ value: email, label: email } as SelectValueLabel)));
        }
    };

    const loadTypes = async () => {
        try {
            const catalog = await GenericTypesService.catalogCharge();
            setTypeOptions([
                // Os tipos de sistema vêm com o nome interno (FUEL, CLEANING…) e traduzem-se por
                // common.generic_types; os criados pela empresa já vêm com o nome do utilizador.
                ...(catalog || []).map(x => ({
                    value: x.value,
                    label: x.isSystem ? (t(`common.generic_types.${x.label}` as any) as string) : (x.label as string),
                } as SelectValueLabel)),
                // Manutenções entram como opção própria; não vêm do catálogo de tipos de despesa.
                { value: MAINTENANCE_OPTION_VALUE, label: t('vehicle.plafonds.maintenances') as string },
            ]);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t load charge types for plafond', error);
            addToast(t('common.messages.error_load_info') as string, { appearance: 'error' });
        }
    };

    const resetForm = () => {
        form.reset({
            designation: plafond?.designation ?? '',
            monthlyLimit: plafond?.monthlyLimit ?? null,
            typeIds: (plafond?.types || []).map(x => (x.kind === PlafondTypeKind.MAINTENANCE ? MAINTENANCE_OPTION_VALUE : (x.typeId as string))),
            notifyEnabled: plafond?.notifyEnabled ?? false,
            contacts: [...(plafond?.contacts || [])],
        });
    };

    const onSubmit = async (model: PlafondFormModel) => {
        // O seletor devolve os ids em bruto: reconstruir as tipologias com o discriminador certo.
        const types = (model.typeIds || []).map(id => (
            id === MAINTENANCE_OPTION_VALUE
                ? { kind: PlafondTypeKind.MAINTENANCE }
                : { kind: PlafondTypeKind.CHARGE_TYPE, typeId: id }
        ));

        // Com a notificação desligada os destinatários não servem para nada — não os gravar.
        const contactEmails = model.notifyEnabled
            ? (model.contacts || []).map(x => x?.trim()).filter(x => !!x)
            : [];

        // Aviso e não erro: falta uma escolha do utilizador, não falhou nada.
        if (model.notifyEnabled && contactEmails.length === 0) {
            addToast(t('vehicle.plafonds.contacts_required') as string, { appearance: 'warning' });
            return;
        }

        const dto: VehicleExpensePlafondDto = {
            id: plafond?.id,
            vehicleId,
            designation: model.designation,
            monthlyLimit: Number(model.monthlyLimit),
            notifyEnabled: model.notifyEnabled,
            types,
            contacts: contactEmails,
        };

        try {
            Loading.show();
            if (plafond?.id) {
                await VehicleExpensePlafondsService.update(dto);
            } else {
                await VehicleExpensePlafondsService.create(dto);
            }
            addToast(t('common.messages.record_save_success') as string, { appearance: 'success' });
            onSaved();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t save the expense plafond', error);
            addToast(t('common.messages.record_save_error') as string, { appearance: 'error' });
        } finally {
            Loading.hide();
        }
    };

    return (
        <Modal
            isOpen={isVisible}
            onRequestClose={onClose}
            shouldCloseOnOverlayClick={true}
            ariaHideApp={false}
            style={{
                // Mesmo molde dos restantes modais do produto: o scroll é do corpo, o cabeçalho e o
                // rodapé ficam fixos. Sem position/inset o ReactModal posiciona-se sozinho e fica torto.
                content: { position: 'relative', inset: 'unset', width: 640, maxWidth: '92vw', padding: 0, overflow: 'hidden' },
            }}
        >
            <form className={styles.modal} onSubmit={form.handleSubmit(onSubmit)}>
                <div className={styles.header}>
                    <h3 className={styles.title}>
                        {plafond?.id ? t('vehicle.plafonds.edit_title') : t('vehicle.plafonds.new_title')}
                    </h3>
                    <p className={styles.subtitle}>{t('vehicle.plafonds.hint') as string}</p>
                </div>

                <div className={styles.body}>
                    <Row>
                        <Col xs={12} md={7}>
                            <FormItem>
                                <Label className={styles.label}>{t('vehicle.plafonds.designation') as string}</Label>
                                <Controller
                                    render={({ onChange, value, ref }) => (
                                        <Input
                                            ref={ref}
                                            form={undefined}
                                            value={value}
                                            placeholder={t('vehicle.plafonds.designation_placeholder') as string}
                                            onChange={(e: any) => onChange(e.target.value)}
                                        />
                                    )}
                                    control={form.control}
                                    name="designation"
                                    defaultValue={''}
                                    rules={{ required: true, maxLength: 150 }}
                                />
                                <InputError error={form.errors.designation} />
                            </FormItem>
                        </Col>

                        <Col xs={12} md={5}>
                            <FormItem>
                                <Label className={styles.label}>{t('vehicle.plafonds.monthly_limit') as string}</Label>
                                <Controller
                                    render={({ onChange, value }) => (
                                        <InputGroup
                                            style={{ textAlign: 'end' }}
                                            format={'number'}
                                            text={'€'}
                                            placeholder={t('vehicle.plafonds.monthly_limit') as string}
                                            value={value}
                                            onValueChange={v => onChange(v)}
                                        />
                                    )}
                                    control={form.control}
                                    name="monthlyLimit"
                                    defaultValue={null}
                                    rules={{ required: true, validate: (v: any) => Number(v) > 0 }}
                                />
                                <InputError error={form.errors.monthlyLimit} />
                            </FormItem>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <FormItem>
                                <Label className={styles.label}>{t('vehicle.plafonds.types') as string}</Label>
                                {/* Controller + Select em vez de SelectController: o multi-select do
                                    projeto é sempre montado assim (ver ChargeFiltersScreen). */}
                                <Controller
                                    render={({ onChange, value }) => (
                                        <Select
                                            options={typeOptions}
                                            isMulti
                                            isClearable
                                            // Sem portal o menu fica cortado pelo corpo do modal.
                                            usePortal
                                            placeholder={t('vehicle.plafonds.types_placeholder') as string}
                                            value={typeOptions.filter(x => (value || []).includes(x.value))}
                                            onChange={(data: SelectValueLabel[]) => onChange(data?.map(d => d.value) ?? [])}
                                        />
                                    )}
                                    control={form.control}
                                    name="typeIds"
                                    defaultValue={[]}
                                    rules={{ validate: (v: any) => !!v && v.length > 0 }}
                                />
                                <InputError error={form.errors.typeIds as any} />
                                <div className={styles.hint}>{t('vehicle.plafonds.types_hint') as string}</div>
                            </FormItem>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <FormItem>
                                <div className={styles.toggleRow}>
                                    <ToggleSwitch name="notifyEnabled" form={form} />
                                    <span className={styles.toggleLabel}>{t('vehicle.plafonds.notify_enabled') as string}</span>
                                </div>
                                <div className={styles.hint}>{t('vehicle.plafonds.notify_hint') as string}</div>
                            </FormItem>
                        </Col>
                    </Row>

                    {/* Destinatários só fazem sentido com a notificação ligada. */}
                    {notifyEnabled && <Row>
                        <Col xs={12}>
                            <FormItem>
                                <Label className={styles.label}>{t('vehicle.plafonds.recipients_label') as string}</Label>
                                {/* Mesmo controlo do envio de anexos de manutenção: carrega os
                                    utilizadores da empresa e deixa escrever endereços novos. */}
                                <Controller
                                    render={({ onChange, value, ref }) => (
                                        <CreateSelect
                                            options={contactOptions}
                                            innerRef={ref}
                                            isClearable
                                            isMulti
                                            placeholder={t('vehicle.plafonds.email') as string}
                                            value={contactOptions.filter(x => (value || []).includes(x.value))}
                                            onChange={(selected: SelectValueLabel[]) => onChange((selected || []).map(x => x.value))}
                                            filterOption={(candidate: any, input: any) => (input
                                                ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase())
                                                : true)}
                                            onCreateOption={(email: string) => {
                                                // O endereço escrito à mão passa a opção, senão o
                                                // select não conseguia mostrá-lo como escolhido.
                                                setContactOptions(prev => [...prev, { value: email, label: email }]);
                                                onChange([...(value || []), email]);
                                            }}
                                        />
                                    )}
                                    control={form.control}
                                    name="contacts"
                                    defaultValue={[]}
                                />
                                <div className={styles.hint}>{t('vehicle.plafonds.recipients_hint') as string}</div>
                            </FormItem>
                        </Col>
                    </Row>}
                </div>

                <div className={styles.footer}>
                    <div className={styles.actions}>
                        <Button preset={'secondary'} type="button" text={t('common.cancel') as string} onClick={onClose} />
                        <Button type={'submit'} text={t('common.save') as string} />
                    </div>
                </div>
            </form>
        </Modal>
    );
};

export default ExpensePlafondModal;
